import React from 'react';
import { Link } from 'react-router-dom';
import { Activity, Code, Brain, BarChart3, FileText, MessageSquare, ArrowRight } from 'lucide-react';
import SEO from '../components/SEO';

const Home = () => {
  return (
    <>
      <SEO
        title={null}
        description="P2P Digital delivers expert consulting in medical device development, AI solutions, and digital health — from concept through regulatory approval to market."
        path="/"
      />
    <div>
      {/* ─── HERO ─── */}
      <div className="relative min-h-screen flex items-center pt-24 overflow-hidden">
        {/* Decorative glow orbs */}
        <div className="absolute w-[500px] h-[500px] bg-cyan-500/10 rounded-full blur-[120px] -top-40 -right-40" />
        <div className="absolute w-[400px] h-[400px] bg-indigo-500/10 rounded-full blur-[120px] bottom-0 -left-40" />

        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left */}
          <div>
            <p className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-cyan-400 mb-4">
              HEALTHCARE &amp; AI INNOVATION
            </p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-tight">
              Engineering the{' '}<br />
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 to-indigo-400">
                Future
              </span>{' '}
              of Healthcare &amp; AI
            </h1>
            <p className="mt-6 text-lg text-slate-400 max-w-xl leading-relaxed">
              We transform visionary ideas into market-ready medical devices, intelligent software, and AI-powered solutions.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/services"
                className="bg-gradient-to-r from-cyan-500 to-indigo-500 rounded-xl px-8 py-4 font-display font-semibold text-white shadow-[0_0_30px_rgba(6,182,212,0.35)] hover:shadow-[0_0_40px_rgba(6,182,212,0.5)] transition-all duration-300"
              >
                Explore Our Services
              </Link>
              <Link
                to="/portfolio"
                className="bg-white/[0.06] backdrop-blur border border-white/[0.1] rounded-xl px-8 py-4 font-display font-semibold text-white hover:bg-white/[0.1] transition-all duration-300"
              >
                View Portfolio
              </Link>
            </div>
          </div>

          {/* Right — abstract neural network visual */}
          <div className="hidden lg:flex items-center justify-center">
            <div className="relative w-[400px] h-[400px]">
              <div className="absolute inset-0 rounded-full border border-cyan-500/20 animate-[spin_30s_linear_infinite]" />
              <div className="absolute inset-8 rounded-full border border-indigo-500/20 animate-[spin_25s_linear_infinite_reverse]" />
              <div className="absolute inset-16 rounded-full border border-cyan-500/15 animate-[spin_20s_linear_infinite]" />
              <div className="absolute inset-24 rounded-full border border-indigo-500/15 animate-[spin_18s_linear_infinite_reverse]" />
              <div className="absolute inset-[44%] rounded-full bg-gradient-to-br from-cyan-500/30 to-indigo-500/30 blur-sm" />
            </div>
          </div>
        </div>
      </div>

      {/* ─── SERVICES ─── */}
      <div className="py-24 max-w-7xl mx-auto px-6">
        <p className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-cyan-400">
          WHAT WE DO
        </p>
        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight mt-3">
          Comprehensive Technology Solutions
        </h2>
        <p className="mt-4 text-slate-400 max-w-2xl text-lg">
          From concept to commercialization, we deliver end-to-end expertise across medical devices, software, and artificial intelligence.
        </p>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            {
              icon: Activity,
              title: 'Medical Device Development',
              description: 'End-to-end device lifecycle from concept to commercialization, with expert regulatory guidance.',
              link: '/services#medical-devices',
            },
            {
              icon: Code,
              title: 'Software Engineering',
              description: 'Scalable custom platforms, SaMD solutions, and cloud-native applications built for the future.',
              link: '/services#software',
            },
            {
              icon: Brain,
              title: 'AI Solutions',
              description: 'Intelligent automation, machine learning, and predictive analytics that drive real business outcomes.',
              link: '/services#ai',
            },
          ].map((card) => {
            const Icon = card.icon;
            return (
              <div
                key={card.title}
                className="bg-white/[0.04] backdrop-blur-xl border border-white/[0.06] rounded-2xl p-8 transition-all duration-300 hover:-translate-y-1 hover:bg-white/[0.07] hover:shadow-[0_20px_60px_rgba(14,165,233,0.12)] group"
              >
                <div className="w-12 h-12 rounded-xl bg-cyan-500/10 flex items-center justify-center mb-6">
                  <Icon className="text-cyan-400 w-6 h-6" />
                </div>
                <h3 className="font-display text-xl font-bold text-white mb-3">{card.title}</h3>
                <p className="text-slate-400 mb-6">{card.description}</p>
                <Link
                  to={card.link}
                  className="text-cyan-400 text-sm font-semibold flex items-center gap-2 group-hover:gap-3 transition-all"
                >
                  Learn More <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            );
          })}
        </div>
      </div>

      {/* ─── SKETCH2SCALE ─── */}
      <div className="py-24 bg-[#111827]">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left */}
          <div>
            <p className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-cyan-400">
              OUR FRAMEWORK
            </p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight mt-3">
              From{' '}
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 to-indigo-400">
                Sketch to Scale
              </span>
            </h2>
            <p className="mt-4 text-slate-400 max-w-xl text-lg">
              Our proprietary framework guides every project through four proven phases, ensuring quality, compliance, and speed to market.
            </p>

            {[
              { num: '1', title: 'Ideation & Validation', desc: 'Market research, feasibility studies, and proof-of-concept development.' },
              { num: '2', title: 'Design & Development', desc: 'User-centred design, prototyping, and iterative engineering.' },
              { num: '3', title: 'Regulatory & Quality', desc: 'Comprehensive QMS, risk management, and regulatory submissions.' },
              { num: '4', title: 'Scale & Commercialization', desc: 'Manufacturing transfer, go-to-market strategy, and post-market support.' },
            ].map((phase) => (
              <div key={phase.num} className="flex gap-4 items-start mt-6">
                <span className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500 to-indigo-500 flex items-center justify-center text-white font-display font-bold text-sm">
                  {phase.num}
                </span>
                <div>
                  <h4 className="font-display font-semibold text-white">{phase.title}</h4>
                  <p className="text-slate-400 text-sm">{phase.desc}</p>
                </div>
              </div>
            ))}

            <Link
              to="/sketch2scale"
              className="text-cyan-400 font-semibold mt-8 inline-flex items-center gap-2 hover:gap-3 transition-all"
            >
              Discover Our Process <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {/* Right — abstract connected nodes */}
          <div className="hidden lg:flex items-center justify-center">
            <div className="relative w-[360px] h-[360px]">
              {/* Connecting lines */}
              <div className="absolute top-[25%] left-[25%] w-[50%] h-[1px] bg-gradient-to-r from-cyan-500/40 to-indigo-500/40 rotate-45 origin-left" />
              <div className="absolute top-[25%] left-[25%] w-[50%] h-[1px] bg-gradient-to-r from-cyan-500/40 to-indigo-500/40 -rotate-45 origin-left" />
              <div className="absolute bottom-[25%] right-[25%] w-[50%] h-[1px] bg-gradient-to-r from-indigo-500/40 to-cyan-500/40 rotate-45 origin-right" />
              <div className="absolute bottom-[25%] right-[25%] w-[50%] h-[1px] bg-gradient-to-r from-indigo-500/40 to-cyan-500/40 -rotate-45 origin-right" />

              {/* Nodes */}
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-16 h-16 rounded-full bg-cyan-500/20 border border-cyan-500/30 flex items-center justify-center text-cyan-400 font-display font-bold">1</div>
              <div className="absolute top-1/2 right-0 -translate-y-1/2 w-16 h-16 rounded-full bg-indigo-500/20 border border-indigo-500/30 flex items-center justify-center text-indigo-400 font-display font-bold">2</div>
              <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-16 h-16 rounded-full bg-cyan-500/20 border border-cyan-500/30 flex items-center justify-center text-cyan-400 font-display font-bold">3</div>
              <div className="absolute top-1/2 left-0 -translate-y-1/2 w-16 h-16 rounded-full bg-indigo-500/20 border border-indigo-500/30 flex items-center justify-center text-indigo-400 font-display font-bold">4</div>
            </div>
          </div>
        </div>
      </div>

      {/* ─── STATS ─── */}
      <div className="py-20 max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {[
            { value: '20+', label: 'Years Experience' },
            { value: '15', label: 'Patents Held' },
            { value: '100+', label: 'Projects Delivered' },
            { value: '3', label: 'Continents Served' },
          ].map((stat) => (
            <div key={stat.label} className="p-6">
              <span className="block text-5xl font-display font-bold bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 to-indigo-400">
                {stat.value}
              </span>
              <span className="block mt-2 text-sm text-slate-400 font-mono uppercase tracking-wider">
                {stat.label}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* ─── AI SOLUTIONS ─── */}
      <div className="py-24 bg-[#111827]">
        <div className="max-w-7xl mx-auto px-6">
          <p className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-cyan-400">
            AI-POWERED INNOVATION
          </p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight mt-3">
            Transform Your Business with Intelligent Solutions
          </h2>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: BarChart3,
                title: 'Business Intelligence & Analytics',
                description: 'Turn raw data into strategic advantage with AI-driven dashboards, forecasting, and actionable insights.',
                link: '/ai-solutions#analytics',
              },
              {
                icon: FileText,
                title: 'Document Processing & Automation',
                description: 'Automate document workflows, extract structured data, and eliminate manual processing bottlenecks.',
                link: '/ai-solutions#documents',
              },
              {
                icon: MessageSquare,
                title: 'Customer Engagement AI',
                description: 'Deliver personalised experiences at scale with conversational AI, sentiment analysis, and smart routing.',
                link: '/ai-solutions#engagement',
              },
            ].map((card) => {
              const Icon = card.icon;
              return (
                <div
                  key={card.title}
                  className="bg-white/[0.04] backdrop-blur-xl border border-white/[0.06] rounded-2xl p-8 transition-all duration-300 hover:-translate-y-1 hover:bg-white/[0.07] hover:shadow-[0_20px_60px_rgba(14,165,233,0.12)] group"
                >
                  <div className="w-12 h-12 rounded-xl bg-cyan-500/10 flex items-center justify-center mb-6">
                    <Icon className="text-cyan-400 w-6 h-6" />
                  </div>
                  <h3 className="font-display text-xl font-bold text-white mb-3">{card.title}</h3>
                  <p className="text-slate-400 mb-6">{card.description}</p>
                  <Link
                    to={card.link}
                    className="text-cyan-400 text-sm font-semibold flex items-center gap-2 group-hover:gap-3 transition-all"
                  >
                    Learn More <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              );
            })}
          </div>

          <div className="mt-12 text-center">
            <Link
              to="/ai-solutions"
              className="inline-block bg-gradient-to-r from-cyan-500 to-indigo-500 rounded-xl px-8 py-4 font-display font-semibold text-white shadow-[0_0_30px_rgba(6,182,212,0.35)] hover:shadow-[0_0_40px_rgba(6,182,212,0.5)] transition-all duration-300"
            >
              Explore AI Solutions
            </Link>
          </div>
        </div>
      </div>

      {/* ─── CTA ─── */}
      <div className="py-24 max-w-7xl mx-auto px-6">
        <div className="relative rounded-3xl overflow-hidden p-16 text-center bg-gradient-to-r from-cyan-600/20 via-indigo-600/20 to-cyan-600/20">
          {/* Glow orbs */}
          <div className="absolute w-[300px] h-[300px] bg-cyan-500/15 rounded-full blur-[100px] -top-20 -left-20" />
          <div className="absolute w-[300px] h-[300px] bg-indigo-500/15 rounded-full blur-[100px] -bottom-20 -right-20" />

          <div className="relative">
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight">
              Ready to Transform Your Vision into Reality?
            </h2>
            <p className="mt-6 text-slate-300 text-lg max-w-2xl mx-auto">
              Partner with us to turn bold ideas into breakthrough products. Let's build something extraordinary together.
            </p>
            <Link
              to="/contact"
              className="inline-block mt-10 bg-gradient-to-r from-cyan-500 to-indigo-500 rounded-xl px-8 py-4 font-display font-semibold text-white shadow-[0_0_30px_rgba(6,182,212,0.35)] hover:shadow-[0_0_40px_rgba(6,182,212,0.5)] transition-all duration-300"
            >
              Get in Touch
            </Link>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default Home;
